import { useLazyUser } from "$global-providers/user-provider";
import { gql } from "@apollo/client";
import type {
  EpocusConsentAudience,
  EpocusConsentItemKind,
  EpocusConsentResponse,
  EpocusPatientConsentSyncPushDocument,
  QueryPullEpocusPatientConsentArgs,
} from "elephant-web/gql";
import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { EpocusPatientConsent } from "../collections/epocus-patient-consent";
import type { RxDBCollections } from "../provider";
import {
  SyncEpocusPatientConsentPullDocument,
  SyncEpocusPatientConsentPushDocument,
  type WholeSyncableEpocusPatientConsentFragment,
} from "./__generated__/epocus-patient-consent.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  EPOCUS_CONSENT_REPLICATION_ROLES,
  isReplicationRoleAllowed,
} from "./replication-role-allowlist";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateEpocusPatientConsent = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<EpocusPatientConsent>;
}) => {
  // pullEpocusPatientConsent requires a single encounterId filter (same
  // shape as pullEpocusExam/pullEpocusBilling), so this only runs while the
  // clinician is looking at one specific encounter.
  const encounterMatch = useMatch("/epocus/encounters/:encounterId");
  const encounterId = encounterMatch?.params.encounterId ?? null;

  const role = useLazyUser()?.role;
  const roleAllowed = isReplicationRoleAllowed(
    EPOCUS_CONSENT_REPLICATION_ROLES,
    role,
  );

  const push = useMemo(
    () => ({
      mutation: SyncEpocusPatientConsentPushDocument,
      mapRxToGql: mapRxEpocusPatientConsentToGqlInput,
    }),
    [],
  );

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableEpocusPatientConsentFragment> | undefined
  >(() => {
    if (!encounterId || !roleAllowed) return undefined;
    return {
      query: SyncEpocusPatientConsentPullDocument,
      reSyncInterval: { minutes: 5 },
      ...createPersistedCheckpointPullBuilders<
        QueryPullEpocusPatientConsentArgs,
        WholeSyncableEpocusPatientConsentFragment
      >({
        buildInputVariables: (checkpoint, batchSize) => ({
          input: { limit: batchSize, filters: { encounterId }, checkpoint },
        }),
        buildPullCheckpoint: (doc) => ({
          id: doc.id,
          updatedAt: doc.meta.updatedAt,
        }),
        entityId: encounterId,
        db,
        collectionName: collection?.name,
      }),
    };
  }, [encounterId, roleAllowed, db, collection]);

  return useGqlReplication<
    EpocusPatientConsent,
    WholeSyncableEpocusPatientConsentFragment,
    EpocusPatientConsentSyncPushDocument
  >({
    db,
    collection,
    push: roleAllowed ? push : undefined,
    pull,
    mapGqlToRx: mapGqlEpocusPatientConsentToRx,
    batchSize: 100,
    expireData: false,
    metadataLocalStorageKeySuffix: "epocus-patient-consent",
  });
};

export const mapGqlEpocusPatientConsentToRx = (
  doc: WholeSyncableEpocusPatientConsentFragment,
): WithDeleted<EpocusPatientConsent> => ({
  id: doc.id,
  encounterId: doc.encounterId,
  elephantPatientIdentityValue: doc.elephantPatientIdentityValue ?? null,
  audience: doc.audience as EpocusPatientConsent["audience"],
  kind: doc.kind as EpocusPatientConsent["kind"],
  response: doc.response as EpocusPatientConsent["response"],
  consentDocumentId: doc.consentDocumentId,
  privacyPolicyId: doc.privacyPolicyId ?? null,
  programId: doc.programId ?? null,
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
  },
  _deleted: doc.meta.deleted,
});

export const mapRxEpocusPatientConsentToGqlInput = (
  doc: WithDeleted<EpocusPatientConsent>,
): EpocusPatientConsentSyncPushDocument => ({
  id: doc.id,
  encounterId: doc.encounterId,
  elephantPatientIdentityValue: doc.elephantPatientIdentityValue,
  audience: doc.audience as EpocusConsentAudience,
  kind: doc.kind as EpocusConsentItemKind,
  response: doc.response as EpocusConsentResponse,
  consentDocumentId: doc.consentDocumentId,
  privacyPolicyId: doc.privacyPolicyId,
  programId: doc.programId,
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
    deleted: doc._deleted,
  },
});

gql`
  fragment WholeSyncableEpocusPatientConsent on SyncableEpocusPatientConsent {
    id
    encounterId
    elephantPatientIdentityValue
    audience
    kind
    response
    consentDocumentId
    privacyPolicyId
    programId
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncEpocusPatientConsentPull($input: PullEpocusPatientConsentInput!) {
    pullEpocusPatientConsent(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableEpocusPatientConsent
      }
    }
  }
`;

gql`
  mutation SyncEpocusPatientConsentPush(
    $input: PushEpocusPatientConsentInput!
  ) {
    pushEpocusPatientConsent(input: $input) {
      rows {
        ...WholeSyncableEpocusPatientConsent
      }
    }
  }
`;
